import * as Yup from 'yup'
import { isNil } from 'lodash'

import {
  awsAccountIdRegex,
  emptySpaceCharsAtStartAndEndRegex,
} from './constants'
import {
  awsAccountIdFieldValidationMessage,
  awsRegexValidationMessage,
  mandatoryFieldMessage,
} from './messagesContants'
import { DateTime } from 'luxon'
import { PlanType } from '@labrav/react-components'
import './globalValidations'

const dimensionsValidation = Yup.array().of(
  Yup.object().shape({
    label: Yup.string().when('isAdditionalUsageAllowed', {
      is: (val: any) => val === false || val == null,
      then: schema =>
        schema.required('Please choose at least one plan to proceed.'),
      otherwise: schema => schema.nullable(),
    }),

    quantity: Yup.number().when('isAdditionalUsageAllowed', {
      is: (val: any) => val === false || val == null,
      then: schema =>
        schema
          .typeError('Please enter a quantity')
          .required('Please enter a quantity')
          .positive('Quantity cannot be negative')
          .integer('Quantity must be a whole number')
          .min(1, 'Quantity must be greater than 0'),
      otherwise: schema => schema.nullable(),
    }),
    prices: Yup.object().when('isAdditionalUsageAllowed', {
      is: (val: any) => val === true,
      then: Yup.object().shape({
        additionalUsagePrice: Yup.number()
          .typeError('Please enter a price')
          .required('Please enter a price')
          .positive('Price cannot be negative')
          .min(0.01, 'Price must be greater than 0')
          .checkDecimalpointValidation({
            message: 'Only 2 decimal places allowed.',
          }),
      }),
      otherwise: Yup.object().nullable(),
    }),
  })
)

const dimensionsSubscriptionValidation = Yup.array().of(
  Yup.object().shape({
    prices: Yup.object().shape({
      subscriptionPrice: Yup.number()
        .typeError('Please enter a price')
        .required('Please enter a price')
        .positive('Price cannot be negative')
        .min(0.01, 'Price must be greater than 0')
        .checkDecimalpointValidation({
          message: 'Only 2 decimal places allowed.',
        }),
    }),
  })
)

export const getCombinedFormikValidationSchema = (productType: string) => {
  const isSubscription = productType === PlanType.SAAS_SUBSCRIPTION

  const resaleAuthFormikValidationSchema = Yup.object().shape({
    //add validation here
    labraCpId: Yup.string().required('Please select a cloud account.'),
    resellerAgreementData: Yup.object().shape({
      awsProductId: Yup.string().required('Please select a product.'),
      companyName: Yup.string().required('Buyer Company name is required.'),
      buyerAwsAccountId: Yup.string()
        .required(mandatoryFieldMessage)
        .matches(emptySpaceCharsAtStartAndEndRegex, `${mandatoryFieldMessage}.`)
        .min(12, awsAccountIdFieldValidationMessage)
        .max(12, awsAccountIdFieldValidationMessage)
        .matches(awsAccountIdRegex, awsRegexValidationMessage),
      renewal: Yup.mixed().test(
        'is-renewal-required',
        'Renewal is required when value is empty.',
        function (value: string | boolean) {
          if (value === undefined) {
            return false
          }
          return true
        }
      ),
      renewalChannel: Yup.string()
        .nullable()
        .when('renewal', {
          is: true,
          then: Yup.string().required('A renewal type is required.'),
          otherwise: Yup.string().nullable(),
        }),
      renewalPricingModel: Yup.string()
        .nullable()
        .when('renewal', {
          is: true,
          then: Yup.string()
            .nullable()
            .required('Select a previous agreement pricing model type.'),
          otherwise: Yup.string().nullable(),
        }),
      dimensions: isSubscription
        ? dimensionsSubscriptionValidation
        : dimensionsValidation,
      contractDurationInMonthsForEndCustomer: isSubscription
        ? Yup.number().nullable()
        : Yup.number()
            .typeError('Please enter a valid number')
            .required('Please enter a number')
            .positive('Duration cannot be negative')
            .integer('Duration must be a whole number')
            .min(1, 'Duration must be greater than 0')
            .test('service-length-with-payment-type', function (value) {
              if (isNil(value)) {
                return true
              }
              const contractDuration = this.parent.contractDurationTimeUnit
              if (contractDuration === 'MONTHS') {
                if (value > 144) {
                  return this.createError({
                    path: this.path,
                    message:
                      'Duration must be less than or equal to 12 years (144 months)',
                  })
                }
              }
              if (contractDuration === 'YEARS') {
                if (value > 12) {
                  return this.createError({
                    path: this.path,
                    message:
                      'Duration must be less than or equal to 12 years (144 months)',
                  })
                }
              }

              return true
            }),

      tcv: isSubscription
        ? Yup.number().nullable()
        : Yup.number()
            .typeError('Please enter a valid total contract value (TCV).')
            .required('Total contract value (TCV) is required.')
            .positive('Total contract value must be greater than zero.')
            .test(
              'reseller-tcv-less-than-cppo',
              'ISV TCV must be less than or equal to buyer TCV.',
              function (value) {
                const formValues = this.options.context
                const cppoTcv = formValues?.cppoData?.tcv
                if (value == null || value === 0)
                  return this.createError({
                    message: 'ISV TCV cannot be zero.',
                  })
                if (cppoTcv == null) return true
                return value <= cppoTcv
              }
            )
            .checkDecimalpointValidation({
              message: 'Only 2 decimal places allowed.',
            }),
      installmentInfo: isSubscription
        ? Yup.array().nullable()
        : Yup.array().of(
            Yup.object().shape({
              amount: Yup.number()
                .typeError('Please enter a valid installment amount.')
                .required('Installment amount is required.')
                .positive('Installment amount must be greater than zero.')
                .checkDecimalpointValidation({
                  message: 'Only 2 decimal places allowed.',
                }),

              paymentDate: Yup.string()
                .required('Date is required.')
                .test(
                  'unique-payment-date',
                  'Duplicate date is not allowed.',
                  function (value) {
                    if (!value) return true
                    const formValues = this.options.context
                    const installments =
                      formValues?.resellerAgreementData?.installmentInfo
                    const matchingDates = installments
                      .map((item: any) => item.paymentDate)
                      .filter((date: string) => date === value)
                    const isDuplicate = matchingDates.length > 1
                    return !isDuplicate
                  }
                )
                .test('date-not-greater-than-max', function (value) {
                  if (!value) return true

                  const formValues = this.options.context
                  const duration =
                    formValues?.resellerAgreementData
                      ?.contractDurationInMonthsForEndCustomer
                  const unitTime =
                    formValues?.resellerAgreementData.contractDurationTimeUnit

                  if (!duration) return true
                  const updatedDuration =
                    unitTime === 'YEARS' ? duration * 12 : duration

                  const currentDate = DateTime.now()
                  const maxDate = currentDate
                    .plus({ months: updatedDuration })
                    .minus({ days: 1 })

                  const selected = DateTime.fromISO(value)

                  if (selected > maxDate) {
                    return this.createError({
                      message: `Date cannot be greater than ${maxDate.toFormat('dd-MM-yyyy')}.`,
                    })
                  }

                  return true
                }),
            })
          ),
    }),

    cppoData: Yup.object().shape({
      offerExpirationDate: Yup.string()
        .nullable()
        .required('Please provide the expiration date.'),
      offerName: Yup.string().required('Offer name is required'),
      subscriptionEndDate: isSubscription
        ? Yup.string()
            .nullable()
            .required('Please provide the usage end date.')
            .test(
              'endDate-after-offerExpirationDate',
              'Usage end date must be after the offer expiration date.',
              function (value?: string | null) {
                const offerExpirationDate = this.parent.offerExpirationDate
                if (!offerExpirationDate || !value) return true
                return (
                  DateTime.fromISO(value) >
                  DateTime.fromISO(offerExpirationDate)
                )
              }
            )
        : Yup.string().nullable(),

      offerDescription: Yup.string()
        .nullable()
        .matches(emptySpaceCharsAtStartAndEndRegex, `${mandatoryFieldMessage}.`)
        .max(255, 'Provide offer description with fewer than 255 characters.'),
      eulaFile: Yup.array().optional().notRequired(),
      variableStartDate: Yup.boolean().nullable(),

      agreementStartDate: Yup.string().when('variableStartDate', {
        is: false,
        then: isSubscription
          ? Yup.string().nullable()
          : Yup.string()
              .nullable()
              .required('Please provide the start date.')
              .test(
                'check-specified-date',
                'Please select a date after offer expiration date.',
                function (value?: string | null) {
                  const offerExpirationDate = this.parent.offerExpirationDate
                  if (offerExpirationDate) {
                    return (
                      DateTime.fromISO(offerExpirationDate) <
                      DateTime.fromISO(value || '')
                    )
                  }
                  return true
                }
              ),
        otherwise: Yup.string().nullable(),
      }),
      adjustmentPercentageInputValue: isSubscription
        ? Yup.number()
            .nullable()
            .typeError('Please enter a valid number')
            .min(1, 'Markup percentage must be greater than 0')
            .test(
              'greater-than-or-equal-zero',
              'Value must be valid.',
              value => value == null || value >= 0
            )
            .required('Please enter a number')
        : Yup.number().nullable(),
      tcv: isSubscription
        ? Yup.number().nullable()
        : Yup.number()
            .typeError('Please enter a valid total contract value (TCV).')
            .required('Total contract value (TCV) is required.')
            .positive('Total contract value must be greater than zero.')
            .test(
              'cppo-tcv-greater-than-reseller',
              'Buyer TCV must be greater than or equal to ISV TCV.',
              function (value) {
                const root = (this as any).from?.[0]?.value
                const formValues = this.options.context
                const resellerTcv = formValues?.resellerAgreementData?.tcv
                if (value == null || value === 0)
                  return this.createError({
                    message: 'Buyer TCV cannot be zero.',
                  })
                if (resellerTcv == null) return true
                return value >= resellerTcv
              }
            )
            .checkDecimalpointValidation({
              message: 'Only 2 decimal places allowed.',
            }),
      installmentInfo: isSubscription
        ? Yup.array().nullable()
        : Yup.array()
            .of(
              Yup.object().shape({
                amount: Yup.number()
                  .typeError('Please enter a valid installment amount.')
                  .required('Installment amount is required.')
                  .positive('Installment amount must be greater than zero.')
                  .test(
                    'reseller-installment-amount-less-than-cppo',
                    'Value cannot be less than ISV price.',
                    function (value, context) {
                      const indexMatch = (this as any).path?.match(/\[(\d+)\]/)
                      const index = indexMatch ? Number(indexMatch[1]) : -1
                      const formValues = this.options.context
                      const resellerInstallment =
                        formValues?.resellerAgreementData?.installmentInfo?.[
                          index
                        ]

                      if (!resellerInstallment) return true

                      if (value == null || value === 0)
                        return this.createError({
                          message: 'Installment amount cannot be zero.',
                        })

                      return value >= resellerInstallment.amount
                    }
                  )
                  .checkDecimalpointValidation({
                    message: 'Only 2 decimal places allowed.',
                  }),
                currency: Yup.string()
                  .required('Currency is required.')
                  .max(3, 'Currency must be a valid 3-letter code.'),
                paymentDate: Yup.string()
                  .required('Payment date is required.')
                  .test(
                    'valid-date',
                    'Payment date must be a valid ISO date.',
                    value => !value || !isNaN(Date.parse(value))
                  ),
              })
            )
            .min(1, 'Please provide at least one installment.'),
    }),
  })

  return resaleAuthFormikValidationSchema
}

export default getCombinedFormikValidationSchema
